import { forwardRef, cloneElement, useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { createPortal } from "react-dom";
import { cva } from "class-variance-authority";
import PropTypes from "prop-types";
import { cn } from "../../utils/cn";

// eslint-disable-next-line react-refresh/only-export-components
export const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-border bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

const Button = forwardRef(
  ({ className, variant, size, asChild = false, children, ...props }, ref) => {
    const classes = cn(buttonVariants({ variant, size, className }));

    if (asChild && children) {
      return cloneElement(children, {
        className: cn(classes, children.props?.className),
        ref,
        ...props,
        ...children.props,
      });
    }

    return (
      <button className={classes} ref={ref} {...props}>
        {children}
      </button>
    );
  }
);

export const ButtonWithArrow = forwardRef(
  ({ className, variant, size, asChild = false, children, options, ...props }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const classes = cn(buttonVariants({ variant, size, className }));
    const dropdownRef = useRef(null);
    const menuRef = useRef(null)

    useEffect(() => {
      const setPosition = () => {
        if (isOpen && menuRef.current && dropdownRef.current) {
          const triggerRect = menuRef.current.getBoundingClientRect();
          const dropdownRect = dropdownRef.current.getBoundingClientRect();

          dropdownRef.current.style.top = `${
            triggerRect.bottom + window.scrollY + 8
          }px`;
          dropdownRef.current.style.left = `${triggerRect.right + window.scrollX - dropdownRect.width}px`;
        }
      };

      setPosition();

      const handleClickOutside = (event) => {
        if (
          menuRef.current &&
          !menuRef.current.contains(event.target) &&
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target)
        ) {
          setIsOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [isOpen]);

    if (asChild && children) {
      return cloneElement(children, {
        className: cn(classes, children.props?.className),
        ref,
        ...props,
        ...children.props,
      });
    }

    return (
      <div ref={menuRef}>
        <button className={classes} {...props} onClick={() => setIsOpen((o) => !o)} ref={ref}>
          {children}
          <ChevronDown
            className={`ml-2 w-4 h-4 text-slate-300 dark:text-gray-300 transition-transform duration-200 ${
              isOpen ? "transform rotate-180" : ""
            }`}
          />
        </button>
        {isOpen &&
          createPortal(
            <div
              ref={dropdownRef}
              style={{ zIndex: 99999 }}
              className="absolute w-min-20 bg-background rounded-md shadow-lg focus:outline-none"
            >
              <div className="py-1">
                {options?.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      option.onClick();
                      setIsOpen(false);
                    }}
                    className="w-full text-left block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-slate-400"
                  >
                    {option.icon && (<option.icon className="mr-2 w-4 h-4"/>)}
                    {option.label}
                  </button>
                ))}
              </div>
            </div>,
            document.body
          )}
      </div>
    );
  }
);

Button.displayName = "Button";

Button.propTypes = {
  className: PropTypes.string,
  variant: PropTypes.oneOf([
    "default",
    "destructive",
    "outline",
    "secondary",
    "ghost",
    "link",
  ]),
  size: PropTypes.oneOf(["default", "sm", "md", "lg", "icon"]),
  asChild: PropTypes.bool,
  children: PropTypes.node,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
    })
  ),
};

export default Button;
